import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
  ACTIVATE_MORATORIUM_EVENT,
  DEACTIVATE_MORATORIUM_EVENT,
  GET_MORATORIUM_EVENT,
} from 'Admin/Partners/graphql';
import { AnimatedScreen, FormInput, PageHeader } from 'Shared/components';
import { DateTime } from 'luxon';
import styles from './styles.module.css';
import MoratoriumsDetailsPostalCodes from './MoratoriumsDetailsPostalCodes';

type MoratoriumEvent = {
  externalId: string;
  name: string;
  eventType: string;
  type: string;
  description: string;
  startDate: string;
  endDate: string;
  active: boolean;
  postalCodes: string[];
};

type MoratoriumEventResponse = {
  moratoriumEvent: MoratoriumEvent;
};

export interface RouteParams {
  id: string;
}

export const MoratoriumDetails: React.FC<RouteComponentProps<RouteParams>> = ({
  match,
  history,
}) => {
  const { data, loading, error } = useQuery<MoratoriumEventResponse>(
    GET_MORATORIUM_EVENT,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        id: match.params.id,
      },
    }
  );

  const [activateMoratoriumEvent, { loading: activating }] = useMutation(
    ACTIVATE_MORATORIUM_EVENT,
    {
      refetchQueries: [
        {
          query: GET_MORATORIUM_EVENT,
          variables: { id: match.params.id },
        },
      ],
    }
  );

  const [deactivateMoratoriumEvent, { loading: deactivating }] = useMutation(
    DEACTIVATE_MORATORIUM_EVENT,
    {
      refetchQueries: [
        {
          query: GET_MORATORIUM_EVENT,
          variables: { id: match.params.id },
        },
      ],
    }
  );

  const moratoriumEvent = data?.moratoriumEvent;

  if (!loading && !moratoriumEvent) {
    history.push('/tools/moratoriums');
    return null;
  }

  const handleDeactivate = () => {
    deactivateMoratoriumEvent({
      variables: { id: match.params.id },
    });
  };

  const handleActivate = () => {
    activateMoratoriumEvent({
      variables: { id: match.params.id },
    });
  };

  return (
    <AnimatedScreen
      testId="moratorium-details"
      loading={loading}
      error={error?.message}
    >
      <PageHeader
        heading="Moratorium Details"
        actionName="Back"
        onActionClick={() => history.push('/tools/moratoriums')}
      />

      {moratoriumEvent && (
        <div className={styles.detailsContainer}>
          <div className={styles.detailRow}>
            <div className={styles.label}>Name</div>
            <div className={styles.value}>{moratoriumEvent.name}</div>
          </div>

          <div className={styles.detailRow}>
            <div className={styles.label}>Event Type</div>
            <div className={styles.value}>{moratoriumEvent.eventType}</div>
          </div>

          <div className={styles.detailRow}>
            <div className={styles.label}>Type</div>
            <div className={styles.value}>{moratoriumEvent.type}</div>
          </div>

          <div className={styles.detailRow}>
            <div className={styles.label}>Description</div>
            <div className={styles.value}>{moratoriumEvent.description}</div>
          </div>

          <div className={styles.detailRow}>
            <div className={styles.label}>Start Date</div>
            <div className={styles.value}>
              {DateTime.fromISO(moratoriumEvent.startDate, {
                zone: 'utc',
              }).toFormat('MM/dd/yyyy hh:mm a')}
            </div>
          </div>

          <div className={styles.detailRow}>
            <div className={styles.label}>End Date</div>
            <div className={styles.value}>
              {DateTime.fromISO(moratoriumEvent.endDate, {
                zone: 'utc',
              }).toFormat('MM/dd/yyyy hh:mm a')}
            </div>
          </div>

          <div className={styles.detailRow}>
            <div className={styles.label}>Active</div>
            <div className={styles.value}>
              <FormInput
                variant="radioButtonGroup"
                name="active-status"
                value={moratoriumEvent.active ? 'active' : 'inactive'}
                onChange={(value: string) => {
                  if (value === 'active') {
                    handleActivate();
                  } else {
                    handleDeactivate();
                  }
                }}
                options={{
                  active: 'Active',
                  inactive: 'Inactive',
                }}
                disabled={activating || deactivating}
              />
            </div>
          </div>

          <MoratoriumsDetailsPostalCodes
            id={match.params.id}
            postalCodes={moratoriumEvent.postalCodes}
          />
        </div>
      )}
    </AnimatedScreen>
  );
};
